import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingCart, Sparkles, Check, CheckCircle2, Lock } from "lucide-react";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/Card";
import { useAuth } from "../contexts/AuthContext";
import { isCartomancerProduct, cartomancerLogoFor } from "../branding";
import SeoHead from "../components/SeoHead";

const StorePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownedIds, setOwnedIds] = useState(new Set());
  const [referralApplied, setReferralApplied] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (user?.user_id) fetchOwnedProducts();
    else setOwnedIds(new Set());
  }, [user?.user_id]);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      localStorage.setItem("mytharion_referral_code", ref.toUpperCase());
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user?.user_id) return;
    const stored = localStorage.getItem("mytharion_referral_code");
    if (!stored) return;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/apply-referral`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ referral_code: stored }),
        });
        if (r.ok) {
          const d = await r.json();
          setReferralApplied(d.referred_by || stored);
        }
        localStorage.removeItem("mytharion_referral_code");
      } catch (e) {
        // ignore
      }
    })();
  }, [user?.user_id]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnedProducts = async () => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/licenses`, {
        credentials: "include",
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const ids = (data.licenses || [])
        .filter((l) => l.status === "active")
        .map((l) => l.product_id)
        .filter(Boolean);
      setOwnedIds(new Set(ids));
    } catch (e) {
      // silent
    }
  };

  const ownsBasic = ownedIds.has("cartomancer_basic") || ownedIds.has("cartomancer_premium");
  const ownsPremium = ownedIds.has("cartomancer_premium");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-16 h-16 border-4 border-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6">
      <SeoHead
        title={t("store.seoTitle")}
        description={t("store.seoDesc")}
        path="/store"
      />
      <div className="container mx-auto max-w-6xl mb-16">
        {referralApplied && (
          <div
            className="mb-6 p-4 rune-border bg-gold/10 border border-gold rounded-xl text-center"
            data-testid="referral-applied-banner"
          >
            <p className="text-sm text-gold font-heading">
              {t("store.referralBanner", { code: referralApplied })}
            </p>
          </div>
        )}
        <div className="text-center mb-12">
          <p className="font-heading text-xs tracking-widest text-gold mb-4">
            {t("store.eyebrow")}
          </p>
          <h1 className="font-display text-5xl md:text-6xl text-foreground mb-6" data-testid="store-title">
            {t("store.title")}
          </h1>
          <p className="font-serif-fantasy italic text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("store.quote")}
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl">
        <div className="grid md:grid-cols-2 gap-8 mb-16 ">
          {products.map((product) => {
            const owned =
              ownedIds.has(product.product_id) ||
              (product.product_id === "cartomancer_basic" && ownsPremium);
            const hasDiscount =
              !!product.discount_active &&
              Number(product.final_price) < Number(product.base_price);
            const moreCount = (product.features || []).length - 4;
            return (
              <Card
                key={product.product_id}
                hover
                className="flex flex-col transition-all duration-700 ease-out hover:drop-shadow-[0_0_35px_rgba(147,51,234,0.9)] hover:scale-[1.02]"
                data-testid={`product-card-${product.product_id}`}
              >
                <Link
                  to={`/product/${product.product_id}`}
                  className="block group/cardlink focus:outline-none focus:ring-2 focus:ring-gold rounded-t-xl "
                  data-testid={`open-product-${product.product_id}`}
                  aria-label={t("store.openProduct", { name: product.name })}
                >
                  <CardHeader>
                    <div className="mb-6 rounded-lg overflow-hidden bg-gradient-to-br from-arcane/20 via-card to-gold/10 h-64 flex items-center justify-center relative ">
                      {(() => {
                        const raw = product.images?.[0];
                        const looksPlaceholder =
                          typeof raw === "string" && raw.includes("placeholder.com");
                        const showImage = raw && !looksPlaceholder;
                        if (showImage) {
                          const src = raw.startsWith("http")
                            ? raw
                            : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
                          return (
                            <img
                              src={src}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                if (isCartomancerProduct(product.product_id)) {
                                  e.currentTarget.src = cartomancerLogoFor(product.product_id);
                                  e.currentTarget.className =
                                    "max-h-56 max-w-[85%] object-contain drop-shadow-[0_8px_20px_rgba(217,178,52,0.35)]";
                                } else {
                                  e.currentTarget.style.display = "none";
                                }
                              }}
                            />
                          );
                        }
                        if (isCartomancerProduct(product.product_id)) {
                          return (
                            <img
                              src={cartomancerLogoFor(product.product_id)}
                              alt={
                                product.product_id === "cartomancer_premium"
                                  ? "Cartomancer Premium"
                                  : "Cartomancer"
                              }
                              className="max-h-56 max-w-[85%] object-contain drop-shadow-[0_8px_20px_rgba(217,178,52,0.35)]"
                              data-testid={`cartomancer-logo-${product.product_id}`}
                            />
                          );
                        }
                        return (
                          <div className="text-center px-4">
                            <Sparkles className="w-12 h-12 text-gold/60 mx-auto mb-2" />
                            <p className="font-display text-2xl text-gold/80">{product.name}</p>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                      <CardTitle className="text-3xl">{product.name}</CardTitle>
                    </div>

                    <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                      <div className="block flex-col items-end gap-1">
                        {product.subscription ? (
                          <Badge variant="arcane">{t("store.badgeSubscription")}</Badge>
                        ) : (
                          <Badge variant="gold">{t("store.badgeOneTime")}</Badge>
                        )}
                        {owned && (
                          <Badge
                            variant="ruby"
                            data-testid={`owned-badge-${product.product_id}`}
                          >
                            {t("store.badgeOwned")}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {product.description}
                    </p>
                  </CardHeader>

                  <CardContent className="flex-1">
                    <div className="space-y-2">
                      {(product.features || []).slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <Check className="w-4 h-4 text-gold mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                      {moreCount > 0 && (
                        <p className="text-xs text-muted-foreground/60 italic pl-6">
                          {t("store.moreFeatures", { count: moreCount })}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Link>

                <CardFooter className="flex items-center justify-between border-t border-border pt-6 gap-4 flex-wrap">
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      {hasDiscount && (
                        <span
                          className="font-heading text-lg text-ruby line-through"
                          data-testid={`price-base-${product.product_id}`}
                        >
                          €{Number(product.base_price).toFixed(2)}
                        </span>
                      )}
                      <span
                        className="font-display text-4xl text-gold"
                        data-testid={`price-final-${product.product_id}`}
                      >
                        €{Number(product.final_price).toFixed(2)}
                      </span>
                      {product.subscription && (
                        <span className="font-heading text-sm text-muted-foreground">
                          {t("store.perMonth")}
                        </span>
                      )}
                      <span
                        className="font-heading text-[10px] uppercase tracking-wider text-muted-foreground"
                        data-testid={`price-tax-hint-${product.product_id}`}
                      >
                        {t("store.vatIncluded")}
                      </span>
                    </div>
                    {!product.subscription && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("store.oneTimePayment")}
                      </p>
                    )}
                  </div>

                  {(() => {
                    const isPremiumProduct = product.product_id === "cartomancer_premium";
                    const requiresBasic = isPremiumProduct && !ownsBasic && user;
                    if (owned) {
                      return (
                        <Link
                          to={`/product/${product.product_id}`}
                          data-testid={`owned-cta-${product.product_id}`}
                        >
                          <Button
                            variant="secondary"
                            size="lg"
                            data-testid={`owned-btn-${product.product_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {t("store.alreadyOwned")}
                          </Button>
                        </Link>
                      );
                    }
                    if (requiresBasic) {
                      return (
                        <Link to={`/product/${product.product_id}`}>
                          <Button
                            variant="secondary"
                            size="lg"
                            data-testid={`requires-basic-${product.product_id}`}
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            {t("store.requiresBasic")}
                          </Button>
                        </Link>
                      );
                    }
                    return (
                      <Link to={`/product/${product.product_id}`}>
                        <Button
                          variant={product.subscription ? "arcane" : "primary"}
                          size="lg"
                          data-testid={`buy-btn-${product.product_id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          {product.subscription ? t("store.subscribe") : t("store.buy")}
                        </Button>
                      </Link>
                    );
                  })()}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        <div className="rune-border bg-card/50 backdrop-blur rounded-2xl p-8 text-center max-w-2xl mx-auto transition-all duration-700 ease-out hover:drop-shadow-[0_0_30px_rgba(255,215,100,0.8)]">
          <Sparkles className="w-12 h-12 text-gold mx-auto mb-4" />
          <h3 className="font-heading text-xl text-gold mb-3">
            {t("store.premiumTrialTitle")}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t("store.premiumTrialDesc")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StorePage;
